import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AdminSideRail, { MobileTopBar, MobileTabBar, MobileDrawer, TopNav, NAV_ITEMS, NAV_CONFIG } from './AdminNavbar';
import AdminTweaks from './AdminTweaks';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { TweaksProvider, useTweaks } from '../context/TweaksContext';
import { useViewport } from '../hooks/useViewport';
import '../admin-sb.css';

// Re-export so other admin components can import from here
export { useTheme as useAdminTheme } from '../../context/ThemeContext';
export { useTweaks } from '../context/TweaksContext';

/** Thin accent-colour progress bar on every route change. */
function RouteProgressBar() {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setProgress(0);
    setVisible(true);
    const t1 = setTimeout(() => setProgress(70), 20);
    const t2 = setTimeout(() => setProgress(100), 250);
    const t3 = setTimeout(() => setVisible(false), 550);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [location.pathname]);

  if (!visible) return null;
  return (
    <div className="sb-progress" style={{ width: `${progress}%` }} />
  );
}

export default function AdminLayout() {
  return (
    <TweaksProvider>
      <AdminShell />
    </TweaksProvider>
  );
}

function AdminShell() {
  const { theme, toggle: toggleTheme } = useTheme();
  const { palette, density, nav } = useTweaks();
  const { api } = useAuth();
  const location = useLocation();
  const { isMobile, isTablet } = useViewport();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sb-rail-collapsed') === 'true'
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refundCount, setRefundCount] = useState(0);

  const isDark = theme === 'dark';

  // Close the mobile drawer on navigation
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Poll pending refund count every 60s to keep badge current
  const fetchRefundCount = useCallback(async () => {
    try {
      const res = await api.get('/api/bookings/', { params: { status: 'cancelled', page_size: 200 } });
      const bookings = res.data.bookings || [];
      setRefundCount(bookings.filter(b => b.refund_eligible && !b.refund_processed).length);
    } catch { /* silently fail */ }
  }, [api]);

  useEffect(() => {
    fetchRefundCount();
    const id = setInterval(fetchRefundCount, 60_000);
    return () => clearInterval(id);
  }, [fetchRefundCount]);

  function toggleCollapsed() {
    setCollapsed(c => {
      const next = !c;
      localStorage.setItem('sb-rail-collapsed', String(next));
      return next;
    });
  }

  // Tablet auto-collapses the rail to icons and locks the toggle
  const effectiveCollapsed = collapsed || isTablet;

  // Active page title for the mobile top bar
  const allNav = [...NAV_ITEMS, ...NAV_CONFIG];
  const activeItem = allNav.find(n =>
    n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to));
  const pageTitle = activeItem?.label || 'Sport Bloc';

  // Shell attributes (shared across all layouts)
  const shellProps = {
    className: 'sb-admin sb-shell',
    'data-mode': isDark ? 'dark' : 'light',
    'data-theme': palette,
    'data-density': density,
    'data-nav': !isMobile && nav === 'top' ? 'top' : undefined,
    'data-rail': effectiveCollapsed ? 'collapsed' : 'expanded',
  };

  // ── Mobile: top bar + bottom tabs + slide-over drawer ──────────────────────
  if (isMobile) {
    return (
      <div {...shellProps}>
        <RouteProgressBar />
        <MobileTopBar title={pageTitle} isDark={isDark} toggleTheme={toggleTheme} onMenu={() => setDrawerOpen(true)} />
        <main className="sb-main"><Outlet /></main>
        <MobileTabBar refundCount={refundCount} onMore={() => setDrawerOpen(true)} />
        <div className={`sb-mobile-scrim ${drawerOpen ? 'open' : ''}`} onClick={() => setDrawerOpen(false)} />
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} refundCount={refundCount} />
        <AdminTweaks />
      </div>
    );
  }

  // ── Top-nav variant (Tweaks → nav="top") ───────────────────────────────────
  if (nav === 'top') {
    return (
      <div {...shellProps}>
        <RouteProgressBar />
        <TopNav isDark={isDark} toggleTheme={toggleTheme} refundCount={refundCount} />
        <main className="sb-main" style={{ maxWidth: 1280, margin: '0 auto', width: '100%' }}>
          <Outlet />
        </main>
        <AdminTweaks />
      </div>
    );
  }

  // ── Desktop / tablet side rail ──────────────────────────────────────────────
  return (
    <div {...shellProps}>
      <RouteProgressBar />
      <AdminSideRail
        collapsed={effectiveCollapsed}
        toggleCollapsed={toggleCollapsed}
        isDark={isDark}
        toggleTheme={toggleTheme}
        refundCount={refundCount}
        lockCollapsed={isTablet}
      />
      <main className="sb-main">
        <Outlet />
      </main>
      <AdminTweaks />
    </div>
  );
}
